import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Custom Bench — per Quoting_markdown/benches.md. Two structurally different
// families (Upholstered to Floor / Bench with Legs) share this one form,
// since only the style list/rates differ — Trim/Nailhead/Delivery are
// identical across both. Submitted through dedicated
// addBenchDraftQuoteObject/updateBenchDraftQuoteObject mutations (see
// DraftQuoteDetail.tsx's DEDICATED_TYPE_CONFIG/handleAddObject), same
// deviation Box Spring Cover/Duvet/etc. already established.
const BenchForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [benchType, setBenchType] = useState<'upholstered_to_floor' | 'with_legs' | ''>((iv?.benchType as any) || '');
  const [benchShape, setBenchShape] = useState<'oval' | 'rectangle' | ''>((iv?.benchShape as any) || '');
  const [benchStyle, setBenchStyle] = useState<string>(iv?.benchStyle || '');
  const [length, setLength] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [width, setWidth] = useState(iv?.depthInches != null ? String(iv.depthInches) : '');
  const [height, setHeight] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [storageOption, setStorageOption] = useState<boolean>(iv?.storageOption || false);
  const [channelSize, setChannelSize] = useState<string>(iv?.channelSize || '');
  const [pillowTopSize, setPillowTopSize] = useState<string>(iv?.pillowTopSize || '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [legSize, setLegSize] = useState<string>(iv?.legSize || '');
  const [legStyle, setLegStyle] = useState<string>(iv?.legStyle || '');
  const [legPrice, setLegPrice] = useState(iv?.legPriceCents != null ? String(iv.legPriceCents / 100) : '');
  const [trimTop, setTrimTop] = useState<boolean>(iv?.trimTop || false);
  const [trimBottom, setTrimBottom] = useState<boolean>(iv?.trimBottom || false);
  const [trimDescription, setTrimDescription] = useState<string>(iv?.trimDescription || '');
  const [nailheadTop, setNailheadTop] = useState<boolean>(iv?.nailheadTop || false);
  const [nailheadBottom, setNailheadBottom] = useState<boolean>(iv?.nailheadBottom || false);
  const [nailheadColor, setNailheadColor] = useState<string>(iv?.nailheadColor || '');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  const isUtf = benchType === 'upholstered_to_floor';
  const isLegs = benchType === 'with_legs';
  const utfStyles = [
    { value: 'standard', label: 'Standard' },
    { value: 'tufted', label: 'Tufted' },
    { value: 'channeled', label: 'Channeled' },
    { value: 'attached_pillow_top', label: 'Attached Pillow Top' },
  ];
  const legsStyles = [
    { value: 'standard', label: 'Standard' },
    { value: 'tufted', label: 'Tufted' },
    { value: 'channeled', label: 'Channeled' },
  ];

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!benchType) { setError('Bench family is required'); return null; }
      if (!benchShape) { setError('Shape is required'); return null; }
      if (!benchStyle) { setError('Style is required'); return null; }
      if (!length) { setError('Length is required'); return null; }
      if (!width) { setError('Width is required'); return null; }
      setError(null);
      return {
        benchType,
        benchShape,
        benchStyle,
        widthInches: parseFloat(length),
        depthInches: parseFloat(width),
        heightInches: height ? parseFloat(height) : null,
        storageOption: isUtf ? storageOption : false,
        channelSize: benchStyle === 'channeled' ? (channelSize.trim() || null) : null,
        pillowTopSize: benchStyle === 'attached_pillow_top' ? (pillowTopSize.trim() || null) : null,
        fabricDescription: fabricDescription.trim() || null,
        legSize: isLegs ? (legSize.trim() || null) : null,
        legStyle: isLegs ? (legStyle.trim() || null) : null,
        legPriceCents: isLegs && legPrice ? Math.round(parseFloat(legPrice) * 100) : null,
        trimTop,
        trimBottom,
        trimDescription: (trimTop || trimBottom) ? (trimDescription.trim() || null) : null,
        nailheadTop,
        nailheadBottom,
        nailheadColor: (nailheadTop || nailheadBottom) ? (nailheadColor.trim() || null) : null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Bench Family</label>
          <select
            className="input"
            value={benchType}
            onChange={(e) => {
              const v = e.target.value as 'upholstered_to_floor' | 'with_legs' | '';
              setBenchType(v);
              setBenchStyle('');
            }}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="upholstered_to_floor">Upholstered to Floor</option>
            <option value="with_legs">Bench with Legs</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Shape</label>
          <select
            className="input"
            value={benchShape}
            onChange={(e) => setBenchShape(e.target.value as 'oval' | 'rectangle' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="oval">Oval</option>
            <option value="rectangle">Rectangle</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Style</label>
          <select
            className="input"
            value={benchStyle}
            onChange={(e) => setBenchStyle(e.target.value)}
            disabled={props.disabled || !benchType}
          >
            <option value="">Select...</option>
            {(isLegs ? legsStyles : utfStyles).map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Length (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={length}
            onChange={(e) => setLength(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Width (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={width}
            onChange={(e) => setWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Height (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(wording only, optional)</span></label>
          <input type="number" min="0" step="0.25" className="input" value={height}
            onChange={(e) => setHeight(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      {benchStyle === 'channeled' && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Channel Size <span style={{ color: '#9ca3af', fontWeight: 400 }}>(shown on quote and work order)</span></label>
            <input type="text" className="input" value={channelSize}
              onChange={(e) => setChannelSize(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      {benchStyle === 'attached_pillow_top' && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Pillow Top Size <span style={{ color: '#9ca3af', fontWeight: 400 }}>(work order only)</span></label>
            <input type="text" className="input" value={pillowTopSize}
              onChange={(e) => setPillowTopSize(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      {isUtf && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>
              <input type="checkbox" checked={storageOption}
                onChange={(e) => setStorageOption(e.target.checked)} disabled={props.disabled} />
              {' '}Storage option (+$350 flat)
            </label>
          </div>
        </div>
      )}

      {isLegs && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Leg Size</label>
            <input type="text" className="input" value={legSize}
              onChange={(e) => setLegSize(e.target.value)} disabled={props.disabled} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Leg Style</label>
            <input type="text" className="input" value={legStyle}
              onChange={(e) => setLegStyle(e.target.value)} disabled={props.disabled} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Leg Price (each, qty fixed at 4)</label>
            <input type="number" min="0" step="0.01" className="input" value={legPrice}
              onChange={(e) => setLegPrice(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={trimTop}
              onChange={(e) => setTrimTop(e.target.checked)} disabled={props.disabled} />
            {' '}Trim around top
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={trimBottom}
              onChange={(e) => setTrimBottom(e.target.checked)} disabled={props.disabled} />
            {' '}Trim around bottom
          </label>
        </div>
        {(trimTop || trimBottom) && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Trim Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(shared by top/bottom)</span></label>
            <input type="text" className="input" value={trimDescription}
              onChange={(e) => setTrimDescription(e.target.value)} disabled={props.disabled} />
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={nailheadTop}
              onChange={(e) => setNailheadTop(e.target.checked)} disabled={props.disabled} />
            {' '}Nailheads around top
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={nailheadBottom}
              onChange={(e) => setNailheadBottom(e.target.checked)} disabled={props.disabled} />
            {' '}Nailheads around bottom
          </label>
        </div>
        {(nailheadTop || nailheadBottom) && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Nailhead Color</label>
            <input type="text" className="input" value={nailheadColor}
              onChange={(e) => setNailheadColor(e.target.value)} disabled={props.disabled} />
          </div>
        )}
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default BenchForm;
